import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ExprCall, Ident } from '@mode-switch/express';
import { EncField, Encoding, EncTopByFieldCondition, VariableType } from '@mode-vegas/vegas';
import { FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledField, CompiledFormula } from '@mode/shared/util-switch';

import * as FieldHelpers from '../helpers/field-helpers';
import { LabeledFieldListMenuComponent } from '../menu/labeled-field-list-menu.component';
import { LabeledMenuComponent } from '@mode/shared/ui';

// Top or Bottom menu items
type TopByFieldOperatorOpt = FieldHelpers.IOpt<EncTopByFieldCondition.Operator>;
const operatorTypeOptions: ReadonlyArray<TopByFieldOperatorOpt> = [
  {
    name: 'Top',
    value: EncTopByFieldCondition.Operator.Top,
  },
  {
    name: 'Bottom',
    value: EncTopByFieldCondition.Operator.Bottom,
  },
];

// Last or First menu items
type TopByFieldSequenceOpt = FieldHelpers.IOpt<EncTopByFieldCondition.Sequence>;
const sequenceTypeOptions: ReadonlyArray<TopByFieldSequenceOpt> = [
  {
    name: 'Apply filter last',
    value: EncTopByFieldCondition.Sequence.LastIsolate,
  },
  {
    name: 'Apply filter first',
    value: EncTopByFieldCondition.Sequence.FirstIsolate,
  },
];

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, LabeledFieldListMenuComponent, LabeledMenuComponent],
  selector: 'mode-rank-panel',
  templateUrl: './rank-panel.component.html',
  styleUrls: ['./rank-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RankPanelComponent implements OnInit, OnChanges {
  @Input() view: FlamingoTypes.ModeView | undefined;
  @Input() encoding: Encoding | undefined;
  @Input() filterField: CompiledFormula | undefined;
  @Input() filterCondition: Partial<EncTopByFieldCondition> | undefined;

  @Output() filterChange = new EventEmitter<EncTopByFieldCondition>();
  @Output() isInvalid = new EventEmitter<boolean>();

  MAX_LIMIT = Math.pow(2, 31) - 1; // Max unsigned int allowed by switch
  TopNSequence = EncTopByFieldCondition.Sequence;

  operator: TopByFieldOperatorOpt = operatorTypeOptions[0];
  sequence: TopByFieldSequenceOpt = sequenceTypeOptions[1];
  operatorMenuItems = operatorTypeOptions;
  sequenceMenuItems = sequenceTypeOptions;

  byFieldAttr: FieldHelpers.CompiledAttrOpt | undefined;
  encodingByFieldAttrs: ReadonlyArray<FieldHelpers.CompiledAttrOpt> = [];
  otherByFieldAttrs: ReadonlyArray<FieldHelpers.CompiledAttrOpt> = [];

  aggrFunc: FieldHelpers.AggFnOpt | undefined;
  allowedAggrFuncs: ReadonlyArray<FieldHelpers.AggFnOpt> = [];

  currentLimit = 0;

  ngOnInit(): void {
    this.sync();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.hasDiffs(changes.filterCondition.currentValue)) {
      this.sync();
    }
  }

  sync(): void {
    this.operator =
      this.operatorMenuItems.find((item) => item.value === this.filterCondition?.operator) ?? this.operatorMenuItems[0];

    this.sequence =
      this.sequenceMenuItems.find((item) => item.value === this.filterCondition?.sequence) ?? this.sequenceMenuItems[0];

    this.currentLimit = this.filterCondition?.limit ?? 0;

    [this.encodingByFieldAttrs, this.otherByFieldAttrs] =
      this.view != null && this.encoding != null
        ? FieldHelpers.makePartitionedByFieldsLists(this.view, this.encoding)
        : [[], []];

    const compiledByField = this.compileField(this.filterCondition?.byField);
    this.byFieldAttr = FieldHelpers.findOrFirst([...this.encodingByFieldAttrs, ...this.otherByFieldAttrs], (attr) => {
      return attr.attrName === compiledByField?.attrName;
    });

    // If the byField matches the filter field, then we can have the special 'No Aggregation' case.
    const isByFieldMatchFilterField = this.isRankByFilterField();
    this.allowedAggrFuncs = this.byFieldAttr
      ? FieldHelpers.aggFnsForFormula(this.byFieldAttr.value, isByFieldMatchFilterField)
      : [];

    const fallbackToDefaultAgg = !isByFieldMatchFilterField;
    const aggrFunc = FieldHelpers.callName(compiledByField, fallbackToDefaultAgg) ?? ExprCall.Name.Any;
    this.aggrFunc = FieldHelpers.findOrFirst(
      this.allowedAggrFuncs,
      (callName) => callName === (aggrFunc ?? compiledByField?.defaultAggregate)
    );

    // Notify if the config is different
    this.emitUpdateIfChanged();
  }

  emitUpdateIfChanged() {
    if (this.filterCondition && this.hasDiffs(this.filterCondition)) {
      this.filterChange.emit(this.constructFilterCondition());
    }
  }

  hasDiffs(condition: Partial<EncTopByFieldCondition>) {
    const compiledByField = this.compileField(condition.byField);

    return (
      this.operator.value !== condition.operator ||
      this.sequence.value !== condition.sequence ||
      this.currentLimit !== condition.limit ||
      this.byFieldAttr?.value.attrName !== compiledByField?.attrName ||
      (this.aggrFunc?.value ?? ExprCall.Name.Any) !== (compiledByField?.aggFunc ?? ExprCall.Name.Any)
    );
  }

  constructFilterCondition() {
    let formula = `[${this.byFieldAttr?.value.attrName}]`;
    if (ExprCall.Name.isKnown(this.aggrFunc?.value ?? '')) {
      formula = `${this.aggrFunc?.value}(${formula})`;
    }

    const byField = {
      id: Ident.next('ENC'),
      formula,
      variable: {
        type: this.isRankByFilterField() ? VariableType.DISCRETE : VariableType.CONTINUOUS,
      },
    };

    const filterCondition: EncTopByFieldCondition = {
      operator: this.operator?.value,
      sequence: this.sequence?.value,
      limit: this.currentLimit ?? 0,
      byField,
    };

    return filterCondition;
  }

  onChangeCondition = (item: TopByFieldOperatorOpt): void => {
    this.operator = item;
    this.emitUpdateIfChanged();
  };

  onChangeField = (item: FieldHelpers.CompiledAttrOpt): void => {
    const prevField = this.byFieldAttr?.value;
    this.byFieldAttr = item;
    // Update the available aggregations on a byField change
    this.allowedAggrFuncs = this.byFieldAttr
      ? FieldHelpers.aggFnsForFormula(this.byFieldAttr.value, this.isRankByFilterField())
      : [];

    // 1. Re-select the applied aggregate function if the varType is changing,
    //    eg. dimension -> measure and vice versa. While the countable options can
    //    be applied to both dimensions and measures, it is more common to apply
    //    one of sum/avg/etc to a measure
    // 2. Previously selected aggregate function cannot be applied to the selected field,
    //    eg. SUM + [price] is okay, SUM + [region] is not. Choose a new aggregation
    if (
      prevField?.varType !== item.value.varType ||
      !this.allowedAggrFuncs.find((aggr) => aggr.value === this.aggrFunc?.value)
    ) {
      const byFieldCallName = FieldHelpers.callName(this.byFieldAttr?.value);
      this.aggrFunc = FieldHelpers.findOrFirst(this.allowedAggrFuncs, (callName) => {
        return callName === byFieldCallName;
      });
    }

    // Emit an update to the filter condition
    this.emitUpdateIfChanged();
  };

  onChangeAggregation = (item: FieldHelpers.AggFnOpt): void => {
    this.aggrFunc = item;
    this.emitUpdateIfChanged();
  };

  onChangeSequence = (item: TopByFieldSequenceOpt): void => {
    this.sequence = item;
    this.emitUpdateIfChanged();
  };

  onLimitChange = (value: number): void => {
    if (value == null || Number.isNaN(value)) {
      this.isInvalid.emit(true);
      return;
    }
    this.currentLimit = Math.min(value, this.MAX_LIMIT);
    this.emitUpdateIfChanged();
    this.isInvalid.emit(false);
  };

  compileField(field: EncField | undefined): CompiledField | undefined {
    return this.view != null && field != null ? CompiledField.fromEncField(this.view, field) : undefined;
  }

  // Validate the user input in the limit number input
  // Only allow number digits and don't allow the value to exceed switch's limit.
  validateInput($event: any) {
    if (
      $event.key &&
      $event.key.length === 1 &&
      (!isFinite($event.key) || $event.currentTarget.valueAsNumber >= this.MAX_LIMIT)
    ) {
      $event.preventDefault();
    }
  }

  isRankByFilterField() {
    return this.byFieldAttr?.value.attrName === this.filterField?.attrName;
  }

  get filterOperatorDisplayValue() {
    return this.operator.value === EncTopByFieldCondition.Operator.Top ? 'top' : 'bottom';
  }
}
